import React from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';

import { AppBar, makeStyles, Toolbar } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { usePermissions } from 'hooks';
import { maskCNPJ, maskCPF } from 'utils/masks';

// Containers
import * as CommandPalette from 'containers/common/CommandPalette';
import { useSpotlightTarget } from 'containers/common/Spotlight';

// Components
import { Logo, Mui } from 'components';
import Label from 'components/layouts/Label';
import Repeater from 'components/layouts/Repeater';

// Local Components
import Account from './Account';
import { TOP_BAR_HEIGHT } from './constants';

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.background.default,
    zIndex: theme.zIndex.drawer + 100,
    color: theme.palette.text.secondary,
  },
  toolbar: { minHeight: TOP_BAR_HEIGHT },
  avatar: { width: 44, height: 44, border: `1px solid ${theme.palette.divider}` },
  link: {
    textDecoration: 'none',
    color: 'inherit',
    '&:link': { textDecoration: 'none', color: 'inherit' },
    '&:visited': { textDecoration: 'none', color: 'inherit' },
    '&:hover': { textDecoration: 'none', color: 'inherit' },
    '&:active': { textDecoration: 'none', color: 'inherit' },
  },
}));

// Render
const renderTaxDocument = value => {
  const hasLetters = /[A-Z]/i.test(value);
  const { length } = String(value).replace(/[^A-Z0-9]/gi, '');
  const isCnpj = hasLetters || length > 11;
  return isCnpj ? `CNPJ ${maskCNPJ(value)}` : `CPF ${maskCPF(value)}`;
};

const usePanelSwitchStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    display: 'inline-grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    alignItems: 'center',
    padding: 2,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },
  thumb: {
    position: 'absolute',
    top: 2,
    bottom: 2,
    left: 2,
    width: 'calc(50% - 2px)',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    transition: theme.transitions.create('transform', {
      duration: theme.transitions.duration.short,
    }),
  },
  thumbRight: {
    transform: 'translateX(100%)',
  },
  button: {
    ...theme.typography.body2,
    position: 'relative',
    zIndex: 1,
    padding: theme.spacing(0.5, 2),
    borderRadius: theme.shape.borderRadius,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  buttonActiveInstitutions: {
    color: theme.palette.context.institutions.main,
    fontWeight: theme.typography.fontWeightBold,
  },
  buttonActiveSellers: {
    color: theme.palette.context.sellers.main,
    fontWeight: theme.typography.fontWeightBold,
  },
}));

function PanelSwitch({ context, displayContext, data, onSelect }) {
  const classes = usePanelSwitchStyles();
  const theme = Mui.useTheme();
  const { permissions } = usePermissions();
  const spotlightRef = useSpotlightTarget('panel-switch-2026-07');

  if (context !== 'institutions' && context !== 'sellers') return null;

  const isSeller = (displayContext || context) === 'sellers';
  const counterpartSlug = context === 'sellers' ? data?.institution?.slug : data?.seller?.slug;

  const { isStaff, isInstitution, isSeller: isSellerUser } = permissions || {};
  const counterpartAllowed = isStaff || (context === 'sellers' ? isInstitution : isSellerUser);

  if (!counterpartAllowed || (data && !counterpartSlug)) {
    const panelColor = theme.palette.context[context]?.main;
    return (
      <Mui.Box display="flex" alignItems="center">
        <Label style={{ color: panelColor, backgroundColor: alpha(panelColor, 0.08) }}>
          {context === 'institutions' ? 'Painel da escola' : 'Painel do vendedor'}
        </Label>
      </Mui.Box>
    );
  }

  return (
    <div className={classes.root} ref={spotlightRef}>
      <span className={clsx(classes.thumb, isSeller && classes.thumbRight)} />
      <Mui.ButtonBase
        className={clsx(classes.button, !isSeller && classes.buttonActiveInstitutions)}
        onClick={() => onSelect('institutions')}
        disabled={!data}
        aria-pressed={!isSeller}>
        Escola
      </Mui.ButtonBase>
      <Mui.ButtonBase
        className={clsx(classes.button, isSeller && classes.buttonActiveSellers)}
        onClick={() => onSelect('sellers')}
        disabled={!data}
        aria-pressed={isSeller}>
        Vendedor
      </Mui.ButtonBase>
    </div>
  );
}

PanelSwitch.propTypes = {
  context: PropTypes.oneOf(['institutions', 'sellers', 'accounts']),
  displayContext: PropTypes.oneOf(['institutions', 'sellers', 'accounts']),
  data: PropTypes.object,
  onSelect: PropTypes.func,
};

function TopBar({
  className,
  children,
  data,
  user,
  onMobileNavOpen,
  homeTo = '/',
  context,
  ...rest
}) {
  const classes = useStyles();
  const theme = Mui.useTheme();
  const history = useHistory();

  const [optimisticContext, setOptimisticContext] = React.useState(null);
  const [prevContext, setPrevContext] = React.useState(context);

  if (context !== prevContext) {
    setPrevContext(context);
    setOptimisticContext(null);
  }

  const displayContext = optimisticContext || context;

  const handlePanelSelect = target => {
    const counterpartSlug = target === 'sellers' ? data?.seller?.slug : data?.institution?.slug;
    if (target === displayContext || !counterpartSlug) return;

    setOptimisticContext(target);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => history.push(`/${target}/${counterpartSlug}`)),
    );
  };

  // Render
  return (
    <AppBar className={clsx(classes.root, className)} elevation={0} {...rest}>
      <Toolbar className={classes.toolbar}>
        <Mui.Hidden mdUp>
          {onMobileNavOpen && (
            <Mui.Box flexGrow={1}>
              <Mui.IconButton color="inherit" onClick={onMobileNavOpen}>
                <Mui.Icon>menu</Mui.Icon>
              </Mui.IconButton>
            </Mui.Box>
          )}
        </Mui.Hidden>
        <Mui.Hidden smDown>
          <Mui.Box
            width={256}
            ml={-3}
            px={2}
            boxSizing="border-box"
            display="flex"
            alignItems="center">
            <RouterLink
              to={homeTo}
              className={classes.link}
              style={{ overflow: 'hidden', width: '100%' }}>
              <Logo
                displayName={data?.displayName}
                src={data?.logo}
                subtitle={data?.taxDocument ? renderTaxDocument(data.taxDocument) : undefined}
                height="24px"
              />
            </RouterLink>
          </Mui.Box>
        </Mui.Hidden>
        <Mui.Hidden mdDown>
          <Mui.Box flexGrow={1} display="flex" justifyContent="center" alignItems="center" mx={2}>
            <CommandPalette.Trigger
              placeholder={
                context === 'sellers' ? 'Buscar menus, pedidos, chamados...' : 'Pesquisar...'
              }
              style={{ maxWidth: 650 }}
              fullWidth
            />
          </Mui.Box>
        </Mui.Hidden>
        <Mui.Box ml="auto">
          <Mui.Box display="flex" alignItems="center" gridGap={theme.spacing(2)}>
            <Mui.Hidden xsDown>
              <PanelSwitch
                context={context}
                displayContext={displayContext}
                data={data}
                onSelect={handlePanelSelect}
              />
            </Mui.Hidden>
            {React.Children.map(children, (child, index) => {
              if (child) return <Mui.Box key={`${index.toString()}`}>{child}</Mui.Box>;
              return null;
            })}
            {user && <Account user={user} />}
          </Mui.Box>
        </Mui.Box>
      </Toolbar>
      <Repeater
        color={theme.palette.context[displayContext]?.main}
        loading={
          (displayContext === 'institutions' || displayContext === 'sellers') &&
          (displayContext !== context || !data?.slug)
        }
      />
    </AppBar>
  );
}

TopBar.propTypes = {
  className: PropTypes.string,
  homeTo: PropTypes.string,
  children: PropTypes.node,
  data: PropTypes.object,
  user: PropTypes.object,
  onMobileNavOpen: PropTypes.func,
  context: PropTypes.oneOf(['institutions', 'sellers', 'accounts']),
};

export default TopBar;
